import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { Checklist } from "models/Checklist";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";
import { useExcludeChecklistMutation } from "app/api/orders";
import { useOrderContext } from "context/OrderContext";
import LargeDialog from "components/LargeDialog";
import { createExcel } from "features/product-list/createExcel";

type ExportProductsListDialogProps = {
  onClose: () => void;
};

const ExportProductsListDialog: React.FC<ExportProductsListDialogProps> = ({ onClose }) => {
  const order = useOrderContext();
  const checklists = order.checklists;
  const { user } = useAuthContext();
  const [includeComments, setIncludeComments] = useState(false);
  const [excludeChecklist] = useExcludeChecklistMutation();

  const userCanExcludeChecklists =
    user?.role === UserRole.ADMIN ||
    user?.role === UserRole.USER_ADMIN ||
    user?.role === UserRole.SALES;

  const checklistsToDownload = useMemo(() => {
    return checklists.filter((checklist) => !checklist.excludedFromReport);
  }, [checklists]);

  const toggleExcludeChecklist = (checklist: Checklist) => {
    if (!checklist.id) return;
    excludeChecklist({
      orderId: order.ordernr,
      checklistId: checklist.id,
      excludedFromReport: !checklist?.excludedFromReport,
    });
  };

  const handleDownload = async () => {
    const blob = await createExcel(order, checklistsToDownload, includeComments);

    const fileUrl = URL.createObjectURL(blob);
    const element = document.createElement("a");
    element.href = fileUrl;
    // Filename
    element.download = `${order?.ordernr} - Produktlista.xlsx`;
    element.click();

    URL.revokeObjectURL(fileUrl);
  };

  return (
    <LargeDialog
      title={`Exportera produktlista för order ${order.ordernr}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="outlined" onClick={onClose} startIcon={<ClearIcon />}>
            Avbryt
          </Button>

          <Button
            variant="contained"
            onClick={handleDownload}
            disabled={checklistsToDownload.length === 0}
            startIcon={<DownloadIcon />}
          >
            Hämta
          </Button>
        </>
      }
    >
      <Box display="grid">
        <FormGroup sx={{ mb: 3 }}>
          <FormLabel sx={{ color: "text.primary", m: 0 }}>Checklistor att inkludera</FormLabel>
          {order.checklists.map((checklist) => {
            return (
              <FormControlLabel
                key={checklist.id}
                label={checklist.roomName}
                control={
                  <Checkbox
                    disabled={!userCanExcludeChecklists}
                    checked={!checklist.excludedFromReport}
                    onChange={() => toggleExcludeChecklist(checklist)}
                  />
                }
              />
            );
          })}
        </FormGroup>

        {/* Feature requested by service department to facilitate large service checkups with many rooms,
         * where short comments are added and directed to customer.
         */}
        <FormGroup>
          <FormControlLabel
            label="Inkludera kommentarer"
            control={
              <Checkbox
                checked={includeComments}
                onChange={(e) => setIncludeComments(e.target.checked)}
              />
            }
          />
          <FormHelperText sx={{ mt: 0 }}>
            OBS: Använd med försiktighet.
            <br />
            Interna kommentarer från checklistor slås ihop och visas i en kolumn.
          </FormHelperText>
        </FormGroup>
      </Box>
    </LargeDialog>
  );
};

export default ExportProductsListDialog;
